import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { services } from '@/mocks/services';

export default function ServicesPage() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Services – Bollywood Firki | Music Production, Recording & Digital Promotion India';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'page-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://bollywoodfirki.com',
      '@type': 'CollectionPage',
      name: 'Bollywood Firki Services – Music Production, Recording & Digital Promotion',
      url: `${import.meta.env.VITE_SITE_URL || ''}/services`,
      description: 'End-to-end music industry services: Music Production, Audio Recording, Mixing & Mastering, Video Production, Artist Management, Digital Promotion, Music Distribution and more.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || ''}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL || ''}/services` },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        name: 'Bollywood Firki Music Services',
        numberOfItems: services.length,
        itemListElement: services.map((svc, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Service',
            name: svc.title,
            description: svc.shortDesc,
            url: `${import.meta.env.VITE_SITE_URL || ''}/services/${svc.slug}`,
            provider: {
              '@type': 'Organization',
              name: 'Bollywood Firki',
            },
          },
        })),
      },
    });
    const existing = document.getElementById('page-jsonld');
    if (existing) existing.remove();
    document.head.appendChild(script);

    const t = setTimeout(() => setVisible(true), 100);
    return () => {
      clearTimeout(t);
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            alt="Bollywood Firki music services"
            className="w-full h-full object-cover object-top"
            src="https://readdy.ai/api/search-image?query=professional%20Indian%20music%20production%20studio%2C%20state%20of%20the%20art%20recording%20equipment%2C%20mixing%20console%2C%20cinematic%20dark%20atmosphere%2C%20golden%20professional%20lighting%2C%20luxury%20music%20studio&width=1920&height=600&seq=svc_hero&orientation=landscape"
          />
          <div className="absolute inset-0 bg-black/85"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#d4af37]"></span>
              <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">What We Offer</span>
              <span className="w-8 h-px bg-[#d4af37]"></span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-4">
              Our <span className="text-[#d4af37]">Services</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              End-to-end music industry services under one roof. From recording to distribution, we handle everything so you can focus on your art.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Link
                key={svc.slug}
                to={`/services/${svc.slug}`}
                className={`group glass-card rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:border-[#d4af37]/40 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${Math.min(i * 60, 500)}ms` }}
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    alt={`${svc.title} - Bollywood Firki`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    src={svc.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center rounded-xl bg-[#d4af37]/20 border border-[#d4af37]/30 backdrop-blur-sm">
                    <i className={`${svc.icon} text-[#d4af37] text-xl`}></i>
                  </div>
                </div>
                <div className="p-6">
                  <h2 className="font-heading text-xl font-bold text-white mb-2 group-hover:text-[#d4af37] transition-colors">{svc.title}</h2>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">{svc.shortDesc}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {svc.benefits.slice(0, 2).map((b) => (
                      <span key={b} className="px-2 py-0.5 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/20 text-[#d4af37] text-xs">{b}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#d4af37] text-sm font-medium">
                    Learn More <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Why Choose <span className="text-[#d4af37]">Bollywood Firki?</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              30+ years of experience, state-of-the-art facilities, and a proven track record of creating India's biggest music hits.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-building-line', title: '30+ Years', desc: 'Three decades of music industry expertise and proven success' },
              { icon: 'ri-team-line', title: '70+ Professionals', desc: 'Dedicated team of producers, engineers, and creative experts' },
              { icon: 'ri-award-line', title: '50+ Awards', desc: 'YouTube Gold, Silver, and industry recognition' },
              { icon: 'ri-global-line', title: 'Global Reach', desc: '100B+ views reaching audiences across 150+ countries' },
            ].map((item) => (
              <div key={item.title} className="glass-card rounded-2xl p-6 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/20 mx-auto mb-4">
                  <i className={`${item.icon} text-[#d4af37] text-2xl`}></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-[#d4af37] mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your <span className="text-[#d4af37]">Project?</span>
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Contact us today and let's discuss how we can bring your musical vision to life.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105">
            Get a Free Consultation <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
